// Shared estimation math — mirrors backend py_pricing logic

// Returns the rate for a role at its default_location (onshore | offshore).
export const roleDefaultRate = (r) =>
  ((r?.default_location || "offshore").toLowerCase() === "onshore"
    ? Number(r.onsite_rate) || 0
    : Number(r.offshore_rate) || 0);

// Resolve hourly rate for a role given a delivery location.
// Falls back to the role's default_location when location is missing or legacy "blended".
export const effectiveRate = (r, location) => {
  const loc = (location || "").toLowerCase();
  if (loc === "onshore") return Number(r.onsite_rate) || 0;
  if (loc === "offshore") return Number(r.offshore_rate) || 0;
  return roleDefaultRate(r);
};

export const locMap = (est) => {
  const defaults = Object.fromEntries(
    (est.roles || []).map((r) => [r.name, (r.default_location || "offshore").toLowerCase()])
  );
  const m = {};
  for (const e of est.load || []) {
    const loc = (e.location || "").toLowerCase();
    const resolved = loc === "onshore" || loc === "offshore" ? loc : (defaults[e.role] || "offshore");
    if (!m[e.workstream]) m[e.workstream] = {};
    m[e.workstream][e.role] = resolved;
  }
  return m;
};

export const taskEffort = (t, sizes) => {
  const o = t.override_hrs;
  if (o !== null && o !== undefined && o !== "" && Number(o) > 0) return Number(o);
  return Number(sizes[t.size]) || 0;
};

export const taskRoleHours = (t, role, sizes) =>
  taskEffort(t, sizes) * (Number((t.role_pcts || {})[role]) || 0);

// `locs` is a location lookup keyed by workstream → role → "onshore" | "offshore"
export const taskCost = (t, roles, sizes, locs) => {
  const wsLoc = (locs && locs[t.workstream]) || {};
  return roles.reduce(
    (s, r) => s + taskRoleHours(t, r.name, sizes) * effectiveRate(r, wsLoc[r.name]),
    0
  );
};

export const pctSum = (t) =>
  Object.values(t.role_pcts || {}).reduce((a, b) => a + (Number(b) || 0), 0);

export function wsSummary(est) {
  const locs = locMap(est);
  return (est.workstreams || []).map((ws) => {
    let hours = 0, cost = 0, totalHours = 0, totalCost = 0;
    for (const t of est.tasks.filter((t) => t.workstream === ws.name)) {
      const e = taskEffort(t, est.tshirt_sizes);
      const c = taskCost(t, est.roles, est.tshirt_sizes, locs);
      const cont = Number(t.contingency_pct) || 0;
      hours += e; cost += c;
      totalHours += e * (1 + cont); totalCost += c * (1 + cont);
    }
    return { ...ws, hours, cost, totalHours, totalCost };
  });
}

export function roleSummary(est) {
  // Per-role cost is location-weighted: for each role, hours are distributed across
  // (workstream, role) load entries — each one priced at its own location rate.
  const locs = locMap(est);
  return est.roles.map((r) => {
    let hours = 0, cost = 0;
    for (const t of est.tasks) {
      const cont = Number(t.contingency_pct) || 0;
      const hr = taskRoleHours(t, r.name, est.tshirt_sizes) * (1 + cont);
      const wsLoc = (locs[t.workstream] || {})[r.name];
      hours += hr;
      cost += hr * effectiveRate(r, wsLoc);
    }
    return { name: r.name, hours, cost };
  });
}

// ── Cost basis: the weekly resource-load grid drives cost ──
// Sum of (entry total hours × effective rate at the row's location) across all load rows.
export function loadTotals(est) {
  const rbn = Object.fromEntries((est.roles || []).map((r) => [r.name, r]));
  let hours = 0, cost = 0;
  for (const e of est.load || []) {
    const r = rbn[e.role];
    if (!r) continue;
    const hrs = (e.hours || []).reduce((a, b) => a + (Number(b) || 0), 0);
    hours += hrs;
    cost += hrs * effectiveRate(r, e.location);
  }
  return { hours, cost };
}

// Load hours & cost rolled up per workstream (cost basis view).
export function wsLoadSummary(est) {
  const rbn = Object.fromEntries((est.roles || []).map((r) => [r.name, r]));
  const agg = {};
  for (const e of est.load || []) {
    const r = rbn[e.role];
    if (!r) continue;
    const hrs = (e.hours || []).reduce((a, b) => a + (Number(b) || 0), 0);
    if (!agg[e.workstream]) agg[e.workstream] = { hours: 0, cost: 0 };
    agg[e.workstream].hours += hrs;
    agg[e.workstream].cost += hrs * effectiveRate(r, e.location);
  }
  return (est.workstreams || []).map((w) => ({
    ...w, hours: agg[w.name]?.hours || 0, cost: agg[w.name]?.cost || 0,
  }));
}

// Load hours & cost rolled up per role (cost basis view).
export function roleLoadSummary(est) {
  const rbn = Object.fromEntries((est.roles || []).map((r) => [r.name, r]));
  return (est.roles || []).map((r) => {
    let hours = 0, cost = 0;
    for (const e of est.load || []) {
      if (e.role !== r.name) continue;
      const hrs = (e.hours || []).reduce((a, b) => a + (Number(b) || 0), 0);
      hours += hrs;
      cost += hrs * effectiveRate(rbn[r.name] || r, e.location);
    }
    return { name: r.name, hours, cost };
  });
}

// ── Suggested baseline: the WBS T-shirt estimate (incl. contingency) ──
// Informational target only; shown as a delta against the load-driven cost.
export function wbsBaseline(est) {
  const locs = locMap(est);
  let base = 0, cont = 0, hrs = 0, thrs = 0;
  for (const t of est.tasks || []) {
    const e = taskEffort(t, est.tshirt_sizes);
    const c = taskCost(t, est.roles, est.tshirt_sizes, locs);
    const tc = Number(t.contingency_pct) || 0;
    base += c; cont += c * tc; hrs += e; thrs += e * (1 + tc);
  }
  return { effortHours: hrs, totalHours: thrs, baseCost: base, totalCost: base + cont };
}

export function pricing(est) {
  // Cost basis comes from the resource-load grid; WBS is a suggested baseline.
  const load = loadTotals(est);
  const wbs = wbsBaseline(est);
  const subtotal = load.cost;
  // Gross margin on final price: final = subtotal / (1 - margin)
  let mpct = Number(est.margin_pct) || 0;
  if (mpct >= 0.99) mpct = 0.99;
  const after = mpct > 0 ? subtotal / (1 - mpct) : subtotal;
  const margin = after - subtotal;
  const fee = after * (Number(est.partner_fee_pct) || 0);
  return {
    effortHours: load.hours, totalHours: load.hours, baseCost: subtotal, contingencyCost: 0,
    subtotal, marginAmount: margin, afterMargin: after, partnerFee: fee, finalPrice: after + fee,
    avgCostRate: load.hours > 0 ? subtotal / load.hours : 0,
    effectiveRate: load.hours > 0 ? (after + fee) / load.hours : 0,
    loadHours: load.hours, loadCost: load.cost,
    suggestedHours: wbs.totalHours, suggestedCost: wbs.totalCost,
    suggestedEffortHours: wbs.effortHours, suggestedBaseCost: wbs.baseCost,
    hoursVariance: load.hours - wbs.totalHours, costVariance: subtotal - wbs.totalCost,
  };
}

// Estimate (incl. contingency) for a role within a workstream
export function roleWsEstimate(est, wsName, roleName) {
  let total = 0;
  for (const t of est.tasks.filter((t) => t.workstream === wsName)) {
    total += taskRoleHours(t, roleName, est.tshirt_sizes) * (1 + (Number(t.contingency_pct) || 0));
  }
  return total;
}

export function loadEntry(est, wsName, roleName) {
  return (est.load || []).find((e) => e.workstream === wsName && e.role === roleName);
}

export function loadStatus(load, estimate, tolPct) {
  if (estimate === 0 && load === 0) return { label: "—", cls: "text-zinc-300" };
  const v = load - estimate;
  if (Math.abs(v) <= Math.max(tolPct * estimate, 1)) return { label: "MATCH", cls: "text-emerald-600" };
  return v > 0
    ? { label: "OVER", cls: "text-red-600" }
    : { label: "UNDER", cls: "text-amber-600" };
}

export const fmtH = (n) => (Math.round(n * 10) / 10).toLocaleString("en-US", { maximumFractionDigits: 1 });
export const fmtC = (n) => "$" + Math.round(n).toLocaleString("en-US");
export const fmtPct = (n) => Math.round((Number(n) || 0) * 100) + "%";
